import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../../components/new-components/ui/button';
import { Input } from '../../../components/new-components/ui/input';
import { Label } from '../../../components/new-components/ui/label';

const MAX_INVESTORS = 5;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SandboxInvestorsStepProps {
  emails: string[];
  onChange: (emails: string[]) => void;
  onContinue: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export function SandboxInvestorsStep({
  emails,
  onChange,
  onContinue,
  onBack,
  onSkip,
}: SandboxInvestorsStepProps) {
  function setEmail(index: number, value: string) {
    const updated = [...emails];
    updated[index] = value;
    onChange(updated);
  }

  function addRow() {
    if (emails.length < MAX_INVESTORS) {
      onChange([...emails, '']);
    }
  }

  function removeRow(index: number) {
    const updated = emails.filter((_, i) => i !== index);
    onChange(updated.length === 0 ? [''] : updated);
  }

  const validCount = emails.filter((e) => EMAIL_PATTERN.test(e)).length;
  const hasInvalidNonEmpty = emails.some((e) => e.length > 0 && !EMAIL_PATTERN.test(e));
  const canContinue = !hasInvalidNonEmpty && validCount > 0;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Invite investors</h2>
        <p className="mt-1 text-muted-foreground">
          Add up to 5 investor emails. They'll receive a link to set up their account and access your sandbox.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Investor Emails</Label>
          <span className="text-xs text-muted-foreground">
            {validCount} of {MAX_INVESTORS} investors added
          </span>
        </div>

        <div className="space-y-3">
          {emails.map((email, index) => {
            const isInvalid = email.length > 0 && !EMAIL_PATTERN.test(email);
            return (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder={`investor${index + 1}@example.com`}
                  value={email}
                  onChange={(e) => setEmail(index, e.target.value)}
                  className={isInvalid ? 'border-red-400 focus-visible:ring-red-400' : ''}
                />
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  disabled={emails.length === 1}
                  className="p-2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30"
                  aria-label="Remove investor"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>

        {emails.some((e) => e.length > 0 && !EMAIL_PATTERN.test(e)) && (
          <p className="text-xs text-red-500">One or more email addresses are invalid.</p>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={addRow}
          disabled={emails.length >= MAX_INVESTORS}
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add investor
        </Button>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Back
        </Button>
        <Button onClick={onContinue} disabled={!canContinue} className="flex-1">
          Continue
        </Button>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
